import type { PermissionSet as GQLPermissionSet } from '../../generated/api.graphql';
import * as Allowance from '../App/TeamRole/Allowance';
import * as PermissionGroup from '../App/TeamRole/PermissionGroup';
import type * as Permission from '../App/TeamRole/Permission';
import type * as TeamRoleDetails from '../App/TeamRole/TeamRoleDetails';
import { PermissionLevel } from '../App/Permission/UserPermission';
import type { UserPermission } from '../App/Permission/UserPermission';
import type { PermissionAttributes } from './ApiKeyInput';

/** The permission matrix an API key may be granted: the column model, capped at the creator's grant. */
export type PermissionScope = {
  permissionGroups: PermissionGroup.PermissionGroup[];
  allowances: Allowance.Allowance[];
};

const ALLOWANCE_PREFIX = 'allowances__';

// `ApiKeyPermissionAttributes` carries 50 of `PermissionKeyEnum`'s 51 keys; `ADMIN__TOOLS` has no
// counterpart, so it must not be minted into a payload the schema would reject.
const UNMAPPED_KEYS = ['admin__tools'];

// `string` rather than `PermissionKey`: it screens both the enum-shaped grant keys and the
// role rows, whose `key` is a free-form string on the schema.
export const attributeKeyFor = (key: string): string | undefined => {
  const attribute = key.toLowerCase().replace(ALLOWANCE_PREFIX, '');

  if (UNMAPPED_KEYS.includes(attribute)) {
    return undefined;
  }

  return attribute;
};

const LEVELS: Record<string, string> = {
  [PermissionLevel.None]: 'none',
  [PermissionLevel.Read]: 'read',
  [PermissionLevel.Edit]: 'edit',
};

const LEVEL_RANKS = [PermissionLevel.None, PermissionLevel.Read, PermissionLevel.Edit].map(level => LEVELS[level]);

const grantsByAttribute = (grants: UserPermission[]) => {
  const byAttribute = new Map<string, string>();

  grants.forEach(grant => {
    const attribute = attributeKeyFor(grant.key);
    if (attribute) {
      byAttribute.set(attribute, LEVELS[grant.level] ?? 'none');
    }
  });

  return byAttribute;
};

// A superadmin holds `edit` even on columns whose options stop at `read`; a cap no option names renders blank yet still submits.
const placeableCap = (permission: Permission.Permission, held: string) => {
  const heldRank = LEVEL_RANKS.indexOf(held);
  let cap = { rank: -1, value: 'none' };

  permission.options.forEach(option => {
    const rank = LEVEL_RANKS.indexOf(option.value.toLowerCase());
    if (rank > cap.rank && rank <= heldRank) {
      cap = { rank, value: option.value };
    }
  });

  return cap.value;
};

export const cappedBy = (
  details: TeamRoleDetails.TeamRoleDetails,
  grants: UserPermission[],
): PermissionScope => {
  const held = grantsByAttribute(grants);

  return {
    permissionGroups: details.permissionGroups.map(group => ({
      ...group,
      permissions: group.permissions.map(permission => ({
        ...permission,
        level: placeableCap(permission, held.get(permission.key) ?? 'none'),
      })),
    })),
    allowances: details.allowances.map(allowance => ({
      ...allowance,
      enabled: (held.get(allowance.key) ?? 'none') !== 'none',
    })),
  };
};

// Keys come from the scope and are screened again: neither a form value nor a role row for a
// key the mutation input lacks can reach the wire, where it would reject the whole payload.
export const toGqlInput = (
  scope: PermissionScope,
  values: Record<string, unknown> = {},
): PermissionAttributes => {
  const attributes: Record<string, unknown> = {};

  scope.permissionGroups.forEach(group => {
    group.permissions.forEach(permission => {
      const attribute = attributeKeyFor(permission.key);
      if (attribute) {
        attributes[attribute] = values[permission.key] ?? permission.level;
      }
    });
  });

  scope.allowances.forEach(allowance => {
    const attribute = attributeKeyFor(allowance.key);
    if (attribute) {
      attributes[attribute] = values[allowance.key] ?? allowance.enabled;
    }
  });

  return attributes as PermissionAttributes;
};

/** What a duplicate seeds the create form with, and which of its keys had to be cut back. */
export type Prefill = {
  values: Record<string, unknown>;
  reducedKeys: string[];
};

export const fromGqlPermissionSet = (item: GQLPermissionSet): PermissionScope => ({
  permissionGroups: item.permissionGroups?.map(PermissionGroup.fromGql) ?? [],
  allowances: item.allowances?.map(Allowance.fromGql) ?? [],
});

// `teamRoleDetails` spells levels lowercase and `currentUser.permissions` uppercase.
const levelIndex = (permission: Permission.Permission, level: string | undefined) =>
  permission.options.findIndex(option => option.value.toLowerCase() === level?.toLowerCase());

/**
 * Mirrors `VPPermissionMatrix.optionsFor`, including its unplaceable-level fallback: a level the
 * options do not name counts as above the cap, because seeding it renders the dropdown blank.
 */
const seedLevel = (permission: Permission.Permission, sourceLevel: string | undefined) => {
  const capIndex = levelIndex(permission, permission.level);
  const cap = permission.options[capIndex]?.value ?? permission.options[0]?.value;
  const sourceIndex = levelIndex(permission, sourceLevel);

  if (sourceIndex < 0 || capIndex < 0 || sourceIndex > capIndex) {
    return { level: cap, reduced: true };
  }

  // `VPDropdown` matches `value` exactly, so a case-only match must not seed the source spelling.
  return { level: permission.options[sourceIndex].value, reduced: false };
};

/**
 * Seeds the create form from a source key, capped at what the caller may grant today. A key the
 * source never granted is seeded at least privilege and is *not* reduced — nothing was lost.
 */
export const prefillFrom = (scope: PermissionScope, source?: PermissionScope): Prefill => {
  if (!source) {
    return { values: {}, reducedKeys: [] };
  }

  const sourceLevels = new Map(
    source.permissionGroups.flatMap(group => group.permissions).map(permission => [permission.key, permission.level]),
  );
  const sourceAllowances = new Map(source.allowances.map(allowance => [allowance.key, allowance.enabled]));

  const values: Record<string, unknown> = {};
  const reducedKeys: string[] = [];

  scope.permissionGroups.forEach(group => {
    group.permissions.forEach(permission => {
      // Not `undefined`: `toGqlInput` resolves a missing value to `permission.level`, the cap.
      if (!sourceLevels.has(permission.key)) {
        values[permission.key] = permission.options[0]?.value ?? 'none';
        return;
      }

      const seeded = seedLevel(permission, sourceLevels.get(permission.key));
      values[permission.key] = seeded.level;
      if (seeded.reduced) {
        reducedKeys.push(permission.key);
      }
    });
  });

  scope.allowances.forEach(allowance => {
    const granted = sourceAllowances.get(allowance.key) ?? false;
    values[allowance.key] = granted && allowance.enabled;
    if (granted && !allowance.enabled) {
      reducedKeys.push(allowance.key);
    }
  });

  return { values, reducedKeys };
};
